import * as React from "react";
import Scrollbar, {
    ScrollbarProps,
    ScrollbarRendererProps,
    ScrollbarThumbRendererProps,
    ScrollbarTrackRendererProps,
} from "react-scrollbars-custom";
import * as styles from "./VerticalScrollbars.less";

const defaultThumbStyle: React.CSSProperties = {
    backgroundColor: "#d3d6e0",
    borderRadius: 4,
    visibility: "visible",
    padding: 0,
};

const darkThumbStyle = {
    ...defaultThumbStyle,
    backgroundColor: "rgb(78, 93, 108)",
};

const defaultTrackStyle: React.CSSProperties = {
    backgroundColor: "#edf0f7",
    borderRadius: 8,
    right: 0,
    top: 4,
    bottom: 4,
    width: 10,
    height: "calc(100% - 8px)",
};

const darkTrackStyle = {
    ...defaultTrackStyle,
    backgroundColor: "rgb(43, 60, 77)",
};

const scrollbarWidth = 10;

const contentStyle: React.CSSProperties = {
    width: "100%",
    msOverflowStyle: "none",
};

type Props = Partial<Omit<ScrollbarProps, "ref">> & {
    darkTheme?: boolean;
    /**
     * indicates that wheel event in edge cases (when user scrolled content to top/bottom edge)
     * should be prevented, i.e. preventing parent scrolling (Sometimes it's annoying when user
     * scrolls some list inside popup with mouse wheel but instead of it parent is scrolled if
     * current list in edge state)
     */
    initPreventWheel?: boolean;
    /**
     * Might be used to fix scrollbar blinking for content with dynamic height
     * when scrollbar should be shown if content height > n and translateContentSizesToHolder
     * is used.
     */
    fixBlinking?: boolean;
};

/**
 * @deprecated use getCustomScrollbarStyles instead this.
 */
export class VerticalScrollbars extends React.Component<Props> {
    _isIE: boolean = false;
    _preventWheel: boolean;

    scrollbarRef: React.RefObject<Scrollbar> = React.createRef();

    constructor(props: Props) {
        super(props);
        this.renderThumb = this.renderThumb.bind(this);
        this.renderTrack = this.renderTrack.bind(this);
        this.renderContent = this.renderContent.bind(this);
        this._preventWheel = props.initPreventWheel;
    }

    componentDidMount() {
        // check if browser is IE/Edge to fix problem with double scroll
        const appVersion = navigator.appVersion;
        this._isIE =
            appVersion.indexOf("Edge") > -1 || appVersion.indexOf("MSIE") > -1 || appVersion.indexOf("Trident") > -1;
        if (this._preventWheel) {
            this.getContentEl().addEventListener("wheel", this.onWheel);
        }
    }

    componentWillUnmount() {
        if (this._preventWheel) {
            this.getContentEl().removeEventListener("wheel", this.onWheel);
        }
    }

    renderThumb(props: ScrollbarThumbRendererProps) {
        const { elementRef, onMouseDown, style, ...restProps } = props;
        const thumbStyle = this.props.darkTheme ? darkThumbStyle : defaultThumbStyle;
        return (
            <div
                {...restProps}
                style={{ ...style, ...thumbStyle }}
                onMouseDown={onMouseDown as any}
                ref={elementRef as any}
            />
        );
    }

    renderTrack(props: ScrollbarTrackRendererProps) {
        const { elementRef, onClick, style, ...restProps } = props;
        const trackStyle = this.props.darkTheme ? darkTrackStyle : defaultTrackStyle;

        return (
            <div {...restProps} style={{ ...style, ...trackStyle }} onClick={onClick as any} ref={elementRef as any} />
        );
    }

    renderWrapper(props: ScrollbarRendererProps) {
        let { elementRef, style, ...restProps } = props;

        // react-scrollbars-custom pass marginRight if Y track is visible
        // but for standard  styles. We customized track styles so we should
        // adjust marginRight according to our track styles.
        if (style.marginRight) {
            style = { ...style, marginRight: scrollbarWidth };
        }

        return <div {...restProps} style={style} ref={elementRef as any} />;
    }

    renderContent(props: ScrollbarRendererProps) {
        const { elementRef, style, ...restProps } = props;
        let inlineStyles = this._isIE && contentStyle;

        // chromium v105 has a bug that native scrollbar that is hidden by parent with overflow:hidden is
        // still clickable (even it's not visible), as a result user clicks on other visible element but nothing happens.
        // React-scrollbars-custom renders content with native scrollbar with margin-right: -scrollbar-width.
        // As a result native scrollbar is hidden and scrolling by mouse wheel works.
        // clip-path with cutting off the vertical scrollbar helps to overcome this bug.
        const nativeScrollFix =
            style.marginRight != null ? { clipPath: `inset(0px ${-style.marginRight}px 0px 0px)` } : null;

        return <div {...restProps} style={{ ...style, ...inlineStyles, ...nativeScrollFix }} ref={elementRef as any} />;
    }

    get scrollTop(): number {
        return this.scrollbarRef.current.scrollTop;
    }

    set scrollTop(value: number) {
        this.scrollbarRef.current.scrollTo(value, 0);
    }

    render() {
        const { darkTheme, initPreventWheel, onScroll, fixBlinking, ...props } = this.props;
        const className = fixBlinking ? styles.fix_blinking : null;
        return (
            <Scrollbar
                className={className}
                noScrollX={true}
                removeTrackYWhenNotUsed={true}
                trackYRenderer={this.renderTrack}
                thumbYRenderer={this.renderThumb}
                wrapperRenderer={this.renderWrapper}
                contentRenderer={this.renderContent}
                onScroll={onScroll}
                ref={this.scrollbarRef}
                {...props}
            />
        );
    }

    getContentEl() {
        // @ts-ignore
        return (this.scrollbarRef.current && this.scrollbarRef.current.contentEl) as HTMLElement;
    }

    onWheel = (e: WheelEvent) => {
        preventWheelInEdgeState(this.getContentEl(), e);
    };
}

export const preventWheelInEdgeState = (container: HTMLElement, e: WheelEvent) => {
    if (!container) {
        return;
    }

    if (e.deltaY < 0) {
        if (container.scrollTop === 0) {
            e.preventDefault();
        }
    } else if (e.deltaY > 0) {
        if (container.scrollTop === container.scrollHeight - container.offsetHeight) {
            e.preventDefault();
        }
    }
};
